import type { FlightSchedule } from "../../../../../my-type";
import { getFightSchedule } from "../../../../../service/TDXService";
import { OrderType } from "../../../../../my-enum";

type ValidationResult =
  | { status: 'valid'; schedule: FlightSchedule; message?: string }
  | { status: 'warning'; message: string }
  | { status: 'invalid'; message: string };

const getExpectedAirportId = (terminal: string) => {
  if (
    terminal === '桃園國際機場第一航廈' ||
    terminal === '桃園國際機場第二航廈'
  ) {
    return 'TPE';
  }
  if (terminal === '台北松山機場' || terminal === '台北送山機場') {
    return 'TSA';
  }
  if (terminal === '台中國際機場' || terminal === '台中國際機場(尚未提供服務)') {
    return 'RMQ';
  }
  if (terminal === '高雄國際機場' || terminal === '高雄國際機場(尚未提供服務)') {
    return 'KHH';
  }
  return null;
};

const getExpectedTerminal = (terminal: string) => {
  if (terminal === '桃園國際機場第一航廈') {
    return '1';
  }
  if (terminal === '桃園國際機場第二航廈') {
    return '2';
  }
  return null;
};

const parseScheduleTime = (time?: string | null) => {
  if (!time) {
    return '';
  }

  return time.match(/^(\d{2}:\d{2})(?:\+\d+)?$/)?.[1] ?? time;
};

const formatCheckTime = (date: Date) =>
  `${`${date.getHours()}`.padStart(2, '0')}:${`${date.getMinutes()}`.padStart(2, '0')}`;

const normalizeFlightSchedules = (value: unknown): FlightSchedule[] | null => {
  if (Array.isArray(value)) {
    return value as FlightSchedule[];
  }

  if (typeof value === 'string') {
    try {
      return normalizeFlightSchedules(JSON.parse(value));
    } catch (error) {
      console.error('Failed to parse flight schedule payload:', error);
      return null;
    }
  }

  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;

  if (Array.isArray(record.$values)) {
    return record.$values as FlightSchedule[];
  }

  if (Array.isArray(record.data)) {
    return record.data as FlightSchedule[];
  }

  if (Array.isArray(record.results)) {
    return record.results as FlightSchedule[];
  }

  return null;
};

export const isFlightNumberValid = (flightNo: string) =>
  /^[A-Z0-9]{2}\d{1,4}$/i.test(flightNo.trim());

export const queryFlightSchedule = async (
  flightNo: string,
  rideTime: Date
): Promise<FlightSchedule[] | null> => {
  const normalizedFlightNo = flightNo.trim().toUpperCase();
  const match = normalizedFlightNo.match(/^([A-Z0-9]{2})(\d{1,4})$/);

  if (!match) {
    return null;
  }

  const iataNo = match[1];
  const flightNumber = match[2];

  const [internationalResult, domesticResult] = await Promise.all([
    getFightSchedule(iataNo, flightNumber, rideTime, true),
    getFightSchedule(iataNo, flightNumber, rideTime, false),
  ]);
  const internationalSchedules = normalizeFlightSchedules(internationalResult) ?? [];
  const domesticSchedules = normalizeFlightSchedules(domesticResult) ?? [];
  const schedules = [...internationalSchedules, ...domesticSchedules];

  return schedules.length > 0 ? schedules : null;
};

export const validateFlightInfo = async ({
  orderType,
  terminal,
  flightNo,
  flightDate,
}: {
  orderType: OrderType;
  terminal: string;
  flightNo: string;
  flightDate: Date;
}): Promise<ValidationResult> => {
  const expectedAirportId = getExpectedAirportId(terminal);
  if (!expectedAirportId) {
    return { status: 'invalid', message: '班機資訊有誤，請確認' };
  }

  const schedules = await queryFlightSchedule(flightNo, flightDate);
  if (!schedules || schedules.length === 0) {
    return { status: 'warning', message: '無法取得班機資訊，無法驗證正確性' };
  }

  const selectedTime = formatCheckTime(flightDate);
  const expectedTerminal = getExpectedTerminal(terminal);
  const matchedSchedule = schedules.find((schedule) => {
    const airportId =
      orderType === OrderType.接機 ? schedule.ArrivalAirportID : schedule.DepartureAirportID;
    if (airportId !== expectedAirportId) {
      return false;
    }

    const scheduleTime =
      orderType === OrderType.接機
        ? parseScheduleTime(schedule.ArrivalTime)
        : parseScheduleTime(schedule.DepartureTime);

    return scheduleTime === selectedTime;
  });

  if (!matchedSchedule) {
    return { status: 'invalid', message: '班機資訊有誤，請確認' };
  }

  if (expectedTerminal && matchedSchedule.Terminal !== expectedTerminal) {
    const terminalMessage = matchedSchedule.Terminal
      ? `航班資料顯示為第 ${matchedSchedule.Terminal} 航廈，與您選擇的航廈不同，請再次確認`
      : '目前無法從航班資料確認航廈，請再次確認';

    return { status: 'valid', schedule: matchedSchedule, message: terminalMessage };
  }

  return { status: 'valid', schedule: matchedSchedule };
};
